import { defineComponent, PropType } from 'vue'
import _ from 'lodash'
import { faLock, faLockOpen, faCircleNotch, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'

import { SEARCH_TYPES } from '@skinsoft/const'
import { ItemTypeWrapper, SearchResult } from '@skinsoft/types'

import { MOVEMENT_ITEM_CARD_LOCATION_CHANGED, UPDATE_ITEM_MOVEMENT_PAGE } from '@skinsoft/events'

import { reactiveVueMixin } from '@skinsoft/reactive-vue'
import { ItemCard, SearchResultItemCard, SpecimenCard } from '@skinsoft/ui'
import { MovementItemCard } from '../movement-item-card'
import { combineLatest } from 'rxjs'

require('./container-card.scss')

declare const config
const ITEM_SEARCH_TYPES: string[] = config.item_search_types

const defaultSortFunction = (items: ItemTypeWrapper[]): ItemTypeWrapper[] => {
    return _.cloneDeep(items)
}

export const ContainerCard = defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        sMovementItemCard: MovementItemCard,
        sItemCard: ItemCard,
        sSpecimenCard: SpecimenCard,
        sSearchResultItemCard: SearchResultItemCard,
    },
    props: {
        container: {},
        inMovementDisplay: {},
        displayType: {},
        canDelete: {},
        sortFunction: {
            type: Function as PropType<(items: ItemTypeWrapper[]) => ItemTypeWrapper[]>,
            default: defaultSortFunction,
        },
    },
    name: 's-container-card',
    template: require('./container-card.html'),
    data() {
        return {
            subItems: [] as ItemTypeWrapper[],
            subSpecimens: [] as ItemTypeWrapper[],
            subContainers: [] as ItemTypeWrapper[],
            subAccessories: [] as ItemTypeWrapper[],

            sortedSubItems: [] as ItemTypeWrapper[],
            sortedSubSpecimens: [] as ItemTypeWrapper[],
            sortedSubContainers: [] as ItemTypeWrapper[],
            sortedSubAccessories: [] as ItemTypeWrapper[],

            isFetchingContent: false,
            contentFetched: false,
            isBeingDeleted: false,
            deleting: false,

            closedContainerIcon: faLock,
            openedContainerIcon: faLockOpen,
            loadingIcon: faCircleNotch,
            checkIcon: faCheck,
            crossIcon: faTimes,

            MOVEMENT_ITEM_CARD_LOCATION_CHANGED,
            UPDATE_ITEM_MOVEMENT_PAGE,
        }
    },
    mounted() {
        this.$root?.$on('bv::collapse::state', (collapseId, isShown) => {
            // @ts-ignore
            if(collapseId && collapseId === this.container.item.id) {
                this.onToggleContent(isShown)
            }
        })
    },
    computed: {
        containerIcon(): IconDefinition | undefined {
            // @ts-ignore we consider that this.container is indeed a container (either Container or SearchResult)
            if (this.container.item.containerItemSchema.openContainer) {
                return this.openedContainerIcon
            }

            // @ts-ignore we consider that this.container is indeed a container (either Container or SearchResult)
            if (this.container.item.containerItemSchema.openContainer === false) {
                return this.closedContainerIcon
            }
        },
    },
    methods: {
        fetchContent() {
            let itemSearchTypes = _.uniq(ITEM_SEARCH_TYPES.concat([SEARCH_TYPES.physicalAccessoryItem, SEARCH_TYPES.physicalContainerItem]))
            this.isFetchingContent = true

            // We combine every type of associated items found into one subscription
            combineLatest([
                // @ts-ignore
                this.$containerDataStore.getAssociatedItems(this.container.item.id, itemSearchTypes),

                // @ts-ignore
                this.$containerDataStore.getAssociatedSpecimens(this.container.item.id),
            ])
            .subscribe(([items, specimens]) => {
                this.subItems = []
                this.subSpecimens = []
                this.subContainers = []
                this.subAccessories = []

                for(const item of items) {
                    switch((item as SearchResult).entityType) {
                        case 'physicalContainerItem':
                            this.subContainers.push({
                                item: item,
                                itemType: 'Container',
                                wrapperType: 'SearchResult',
                            })
                            continue
                        case 'physicalAccessoryItem':
                            this.subAccessories.push({
                                item: item,
                                itemType: 'Accessory',
                                wrapperType: 'SearchResult',
                            })
                            continue
                        default:
                            this.subItems.push({
                                item: item,
                                itemType: 'Item',
                                wrapperType: 'SearchResult',
                            })
                            continue
                    }
                }

                for (const specimen of specimens) {
                    this.subSpecimens.push({
                        item: specimen,
                        itemType: 'Specimen',
                        wrapperType: 'Specimen'
                    })
                }

                this.applySort()

                this.contentFetched = true
                this.isFetchingContent = false
            })
        },
        applySort() {
            this.sortedSubItems = this.sortFunction(this.subItems)
            this.sortedSubSpecimens = this.sortFunction(this.subSpecimens)
            this.sortedSubContainers = this.sortFunction(this.subContainers)
            this.sortedSubAccessories = this.sortFunction(this.subAccessories)
        },
        onToggleContent(contentShown: boolean) {
            if(!this.contentFetched && contentShown) {
                this.fetchContent()
            }
        },
        onLongPress() {
            if(this.canDelete) {
                this.isBeingDeleted = !this.isBeingDeleted
            }
        },
        onDelete() {
            this.deleting = true
            this.$emit('delete', this.container)
        },
        onCancelDelete() {
            this.isBeingDeleted = false
        },
    },
    watch: {
        container() {
            this.isBeingDeleted = false
        },
        sortFunction() {
            this.applySort()
        },
    },
})
